/**
 *
 * CommunicationHeader
 *
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useLocation } from 'react-router-dom';

// styles
import './style.scss';

// assets
import { DownOutlined, PrinterOutlined } from '@ant-design/icons';
import CustomIcons from 'components/CustomIcons';

// antd component
import { Form, Dropdown, Menu, Input, Button, Tooltip } from 'antd';
// components
import CustomPageHeader from 'components/CustomPageHeader';
import GoBackButton from 'components/GoBackButton';
import TCFModal from 'components/TCFModal';

// utils
import history, { parseQuery, encodeQueryData } from 'utils/history';
import { isDateBeforeToday, converDateToString } from 'utils/moment/moment.utils';

function CommunicationHeader({
  form,
  mode,
  method,
  secondaryForm,
  loadingList,
  isReminderLoading,
  senderRole,
  // function
  actionDropdownCallback,
  // states
  types,
  showReminder,
  name,
  subTitle,
  subTitlePosition,
  submitLabel,
  actionDropdownLabel,
  // actions
  onValuesChange,
  onSubmit,
  // default props
  uniqueKey,
  className
}) {
  const childClassNames = classNames('communications-header', className);

  const newKey = 'nouvel';
  const location = useLocation();

  const hasFormOrAppointment =
    !!types && types?.some(el => el == 'forms' || el == 'appointments');

  const [currentMode, setCurrentMode] = useState(mode);

  useEffect(() => {
    setCurrentMode(mode);
  }, [mode]);

  const goToEditModeCallback = useCallback(() => {
    const searchParams = parseQuery(location.search) || null;
    let currentData = searchParams;
    if (searchParams.hasOwnProperty('preview')) {
      delete currentData['preview'];
    }
    const { page, ...searchData } = currentData;
    const serachStr = encodeQueryData(searchData);
    history.push({
      search: `?${serachStr}`,
      state: {
        from: 'preview_communication',
        preview: false,
        params: currentData,
      },
    });
  }, [history, location.search]);

  const actionsMenu = useMemo(() => {
    const secondaryFormValues = secondaryForm.getFieldsValue();
    const deadlineDate = secondaryFormValues?.deadlineDate;
    const expiredDeadlineDate = isDateBeforeToday(deadlineDate);
    return (
      <Menu onClick={actionDropdownCallback}>
        {currentMode != 'onlyRead' && (
          <>
            <Menu.Item key="save-draft">Enregistrer comme brouillon</Menu.Item>
            <Menu.Item key="preview">Prévisualiser</Menu.Item>
          </>
        )}
        <Menu.Item key="print">Imprimer</Menu.Item>
        {method != newKey && (
          <>
            <Menu.Item
              key="copy-audience"
              disabled={!(currentMode == 'onlyRead')}
            >
              Sauvegarder l’audience
            </Menu.Item>
            {!!hasFormOrAppointment && mode == 'onlyRead' && (
              <Menu.Item
                key="reminder"
                disabled={
                  (!!deadlineDate && !expiredDeadlineDate) ||
                  (!deadlineDate && !showReminder) ||
                  isReminderLoading
                }
              >
                {
                  ((!!deadlineDate && !expiredDeadlineDate) || (!deadlineDate && !showReminder)) ? (
                    <Tooltip
                      placement="top"
                      title="Le rappel n'a pas encore expiré."
                      color="#005D9F"
                      trigger="click"
                    >
                      Relancer
                    </Tooltip>
                  ) : "Relancer"
                }
                
              </Menu.Item>
            )}
            <Menu.Item key="dublicate" disabled={senderRole == 'parent' || loadingList}>
              Dupliquer
            </Menu.Item>
          </>
        )}
        {currentMode != 'onlyRead' && (
          <Menu.Item key="delete" disabled={loadingList} danger>
            Supprimer
          </Menu.Item>
        )}
      </Menu>
    );
  }, [
    currentMode,
    hasFormOrAppointment,
    actionDropdownCallback,
    method,
    mode,
    secondaryForm,
    showReminder,
    senderRole
  ]);

  const onFinishCallback = useCallback(
    async values => {
      !!onSubmit && onSubmit(values);
    },
    [onSubmit],
  );

  const changeHeaderForm = useCallback((changedValues, allValues) => { 
    if (!!changedValues?.title) {
      secondaryForm.setFieldsValue({
        title: changedValues?.title
      });
    }
    !!onValuesChange && onValuesChange(changedValues, allValues, 'header');
  }, []);

  return (
    form && (
      <>
        <CustomPageHeader
          className={childClassNames}
          title={
            <>
              {currentMode != 'preview' && (
                <Form
                  key={`${name}-form-header-${uniqueKey}`}
                  form={form}
                  name={name}
                  onValuesChange={changeHeaderForm}
                  autoComplete="off"
                  className={`communication-header-form ${
                    currentMode != 'onlyRead' ? 'mt-6' : ''
                  }`}
                  onFinish={onFinishCallback}
                >
                  <Form.Item
                    name="title"
                    className={
                      currentMode == 'onlyRead' ? 'show-as-titile' : ''
                    }
                    rules={[
                      {
                        type: 'string',
                        min: 2,
                        required: true,
                        message: 'Champs obligatoire',
                        validateTrigger: 'onSubmit',
                      },
                    ]}
                  >
                    <Input
                      placeholder="Titre de la communication"
                      disabled={currentMode == 'onlyRead'}
                    />
                  </Form.Item>
                  <Dropdown
                    className="communication-actions"
                    trigger={['click']}
                    overlay={actionsMenu}
                    getPopupContainer={trigger => trigger.parentElement}
                    overlayClassName="communication-actions__menu"
                  >
                    <Button>
                      <span className="dropdownLabel">
                        {actionDropdownLabel}
                      </span>
                      <DownOutlined />
                    </Button>
                  </Dropdown>
                  {currentMode != 'onlyRead' && (
                    <Form.Item className="submit-wrapper">
                      <Button
                        type="primary"
                        // className={`${disabeled ? 'disabeled' : ''}`}
                        htmlType="submit"
                        size={'large'}
                      >
                        {submitLabel}
                      </Button>
                    </Form.Item>
                  )}
                </Form>
              )}
              {currentMode == 'preview' && (
                <>
                  Prévisualisation
                  {currentMode == 'preview' && (
                    <GoBackButton key={`go-back-${uniqueKey}`} onGoBack={goToEditModeCallback} />
                  )}
                </>
              )}
            </>
          }
          subTitle={subTitle}
          subTitlePosition={subTitlePosition}
          //extraActions={[]}
        />
      </>
    )
  );
}

CommunicationHeader.defaultProps = {
  name: 'communicationHeaderForm',
  subTitle: 'Communication',
  subTitlePosition: 'top',
  submitLabel: 'Envoyer',
  actionDropdownLabel: 'Actions',
  mode: 'edit',
  method: 'update',
  showReminder: true,
};
CommunicationHeader.propTypes = {
  mode: PropTypes.oneOf(['edit', 'onlyRead', 'preview', 'new']),
  method: PropTypes.oneOf(['update', 'nouvel']),
  types: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.string,
    PropTypes.array,
  ]),
  form: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.string,
    PropTypes.object,
  ]),
  actionDropdownCallback: PropTypes.func,
};

export default CommunicationHeader;
